import * as React from 'react';
import { Box, Boxes, FileCheck2, History } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import {
  ORDER_STATUS,
  ORDER_STATUS_REASON,
  type HistoryEntry,
  type Order,
  type Task,
} from '@klareospace/pharos-lib-types';
import { ErrorBanner, SideDrawer, Tabs, TabsContent, TabsList, TabsTrigger } from '@klareospace/pharos-lib-web-utils';

import { TaskDetailDialog, TasksSection, useTaskDetailDialog } from '&/features/tasks';

import type { OrderHistoryResult } from '../api';
import { composeStatusErrorMessage } from '../utils/status-reason';
import { useOrder } from '../hooks/use-order';
import { useOrderHistory } from '../hooks/use-order-history';
import { useOrderStatusHistory } from '../hooks/use-order-status-history';
import { useOrderTasks } from '../hooks/use-order-tasks';
import { ContentSummarySection } from './content-summary-section';
import { ContentTab } from './content-tab';
import { DrawerHeader } from './drawer-header';
import { InstructionSection } from './instruction-section';
import { LifecycleSection } from './lifecycle-section';
import { OrderDetailSection } from './order-detail-section';
import { OrderHistorySection } from './order-history-section';
import { OrderProofsTab } from './order-proofs-tab';
import { PickupDeliverySection } from './pickup-delivery-section';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface OrderDetailDrawerProps {
  /** The order row data from the table (used for immediate rendering before detail loads) */
  order: Order | null;
  onClose: () => void;
  open: boolean;
}

// ─── Constants ────────────────────────────────────────────────────────────────

const DRAWER_TAB = { CONTENT: 'content', HISTORY: 'history', OVERVIEW: 'overview', PROOFS: 'proofs' } as const;
type DrawerTab = (typeof DRAWER_TAB)[keyof typeof DRAWER_TAB];

// ─── Helpers ──────────────────────────────────────────────────────────────────

/**
 * Reasons that have a dedicated per-section error display.
 * When present, the generic banner at the top of the Overview tab is suppressed.
 */
const SECTION_HANDLED_REASONS: ReadonlySet<string> = new Set([
  ORDER_STATUS_REASON.GEOCODING_FAILED,
  ORDER_STATUS_REASON.METRICS_VALIDATION_FAILED,
]);

/** Returns true when the order requires a generic anomaly banner in the Overview tab. */
function isGenericBannerVisible(order: Order | null): boolean {
  return (
    order !== null && order.status === ORDER_STATUS.ANOMALY && !SECTION_HANDLED_REASONS.has(order.statusReason ?? '')
  );
}

/** Resolves an incoming `Tabs` value to a known `DrawerTab`, defaulting to Overview for unrecognized values. */
function resolveDrawerTab(value: string): DrawerTab {
  if (value === DRAWER_TAB.CONTENT) return DRAWER_TAB.CONTENT;
  if (value === DRAWER_TAB.HISTORY) return DRAWER_TAB.HISTORY;
  if (value === DRAWER_TAB.PROOFS) return DRAWER_TAB.PROOFS;
  return DRAWER_TAB.OVERVIEW;
}

/** Extracts the `items` page of a history query result — isolates the optional-chain from `OrderDetailDrawer`. */
function toHistoryItems(page: OrderHistoryResult | undefined): HistoryEntry[] | undefined {
  return page?.items;
}

// ─── Sub-components ───────────────────────────────────────────────────────────

interface OverviewTabProps {
  readonly displayOrder: Order;
  readonly isDetailLoading: boolean;
  readonly isStatusHistoryError: boolean;
  readonly isStatusHistoryLoading: boolean;
  readonly isTasksLoading: boolean;
  readonly onTaskClick: (task: Task) => void;
  readonly onViewContent: () => void;
  readonly statusHistory: HistoryEntry[] | undefined;
  readonly tasks: Task[] | undefined;
}

/** Overview tab body — extracted to keep OrderDetailDrawer's cyclomatic complexity low. */
function OverviewTab({
  displayOrder,
  isDetailLoading,
  isStatusHistoryError,
  isStatusHistoryLoading,
  isTasksLoading,
  onTaskClick,
  onViewContent,
  statusHistory,
  tasks,
}: OverviewTabProps): React.JSX.Element {
  const { t } = useTranslation('orders');

  return (
    <>
      {isGenericBannerVisible(displayOrder) && (
        <ErrorBanner
          message={composeStatusErrorMessage(t, t('drawer.errors.orderAnomaly'), displayOrder.statusReason)}
        />
      )}
      {displayOrder.status !== ORDER_STATUS.ANOMALY && (
        <LifecycleSection
          history={statusHistory}
          isError={isStatusHistoryError}
          isLoading={isStatusHistoryLoading}
          order={displayOrder}
        />
      )}
      <OrderDetailSection isLoading={isDetailLoading} order={displayOrder} />
      <PickupDeliverySection order={displayOrder} />
      <ContentSummarySection onViewMore={onViewContent} order={displayOrder} />
      <InstructionSection order={displayOrder} />
      <TasksSection isLoading={isTasksLoading} onTaskClick={onTaskClick} tasks={tasks} />
    </>
  );
}

// ─── Component ────────────────────────────────────────────────────────────────

export function OrderDetailDrawer({ onClose, open, order }: OrderDetailDrawerProps): React.JSX.Element {
  const { t: tCommon } = useTranslation('common');
  const { t } = useTranslation('orders');
  const [activeTab, setActiveTab] = React.useState<DrawerTab>(DRAWER_TAB.OVERVIEW);
  const { closeTaskDetail, isTaskDetailOpen, openTaskDetail, selectedTask } = useTaskDetailDialog();

  const orderId = order?.id ?? '';
  const isEnabled = open && orderId.length > 0;

  const { data: orderDetail, isLoading: isDetailLoading } = useOrder(orderId, { enabled: isEnabled });
  const {
    data: historyPage,
    isError: isHistoryError,
    isLoading: isHistoryLoading,
    refetch: refetchHistory,
  } = useOrderHistory(orderId, { enabled: isEnabled });
  const {
    data: statusHistoryPage,
    isError: isStatusHistoryError,
    isLoading: isStatusHistoryLoading,
  } = useOrderStatusHistory(orderId, { enabled: isEnabled });
  const { data: tasks, isLoading: isTasksLoading } = useOrderTasks(orderId, { enabled: isEnabled });
  const history = toHistoryItems(historyPage);
  const statusHistory = toHistoryItems(statusHistoryPage);

  const displayOrder = orderDetail ?? order;

  const headerContent =
    displayOrder !== null ? (
      <DrawerHeader copiedTooltip={t('drawer.copied')} copyTooltip={t('drawer.copy')} order={displayOrder} />
    ) : undefined;

  function handleOpenChange(nextOpen: boolean): void {
    if (!nextOpen) {
      onClose();
      setActiveTab(DRAWER_TAB.OVERVIEW);
      closeTaskDetail();
    }
  }

  return (
    <>
      <SideDrawer
        autoFocus={false}
        headerContent={headerContent}
        onOpenChange={handleOpenChange}
        open={open}
        title={order?.referenceNumber ?? ''}
        widthClassName="sm:max-w-4xl"
      >
        {displayOrder !== null && (
          <Tabs onValueChange={(v): void => setActiveTab(resolveDrawerTab(v))} value={activeTab}>
            <TabsList className="mb-4" size="sm">
              <TabsTrigger size="sm" value={DRAWER_TAB.OVERVIEW}>
                <Box className="h-4 w-4" />
                {t('drawer.tabs.overview')}
              </TabsTrigger>
              <TabsTrigger size="sm" value={DRAWER_TAB.CONTENT}>
                <Boxes className="h-4 w-4" />
                {tCommon('fields.content')}
              </TabsTrigger>
              <TabsTrigger size="sm" value={DRAWER_TAB.PROOFS}>
                <FileCheck2 className="h-4 w-4" />
                {tCommon('fields.proofsTitle')}
              </TabsTrigger>
              <TabsTrigger size="sm" value={DRAWER_TAB.HISTORY}>
                <History className="h-4 w-4" />
                {tCommon('fields.historyTitle')}
              </TabsTrigger>
            </TabsList>

            {/* ── Overview tab ──────────────────────────────────────────── */}
            <TabsContent className="space-y-4" value={DRAWER_TAB.OVERVIEW}>
              <OverviewTab
                displayOrder={displayOrder}
                isDetailLoading={isDetailLoading}
                isStatusHistoryError={isStatusHistoryError}
                isStatusHistoryLoading={isStatusHistoryLoading}
                isTasksLoading={isTasksLoading}
                onTaskClick={openTaskDetail}
                onViewContent={(): void => setActiveTab(DRAWER_TAB.CONTENT)}
                statusHistory={statusHistory}
                tasks={tasks}
              />
            </TabsContent>

            {/* ── Content tab ────────────────────────────────────────────── */}
            <TabsContent value={DRAWER_TAB.CONTENT}>
              <ContentTab order={displayOrder} />
            </TabsContent>

            {/* ── Proofs tab ─────────────────────────────────────────────── */}
            <TabsContent value={DRAWER_TAB.PROOFS}>
              <OrderProofsTab isLoading={isTasksLoading} tasks={tasks} />
            </TabsContent>

            {/* ── History tab ────────────────────────────────────────────── */}
            <TabsContent value={DRAWER_TAB.HISTORY}>
              <OrderHistorySection
                history={history}
                isError={isHistoryError}
                isLoading={isHistoryLoading}
                onRetry={(): void => void refetchHistory()}
              />
            </TabsContent>
          </Tabs>
        )}
      </SideDrawer>
      <TaskDetailDialog
        onClose={closeTaskDetail}
        onTaskUpdated={openTaskDetail}
        open={isTaskDetailOpen}
        task={selectedTask}
      />
    </>
  );
}
